import React, { useEffect, useState } from "react";
import { useProjectManagementContext } from "../context";
import { providerTermijnService } from "../../../api/manpro/providerTermijnService";
import type { TermijnDto } from "../../../api/manpro/types";
import toast from "react-hot-toast";
import { CircleDollarSign, CheckCircle, Wallet, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import Modal from "../../../components/Modal";
import axiosInstance from "../../../api/axios";
import { isInternalManproProject } from "../isInternalManproProject";
import FinanceTabSkeleton from "../../../components/FinanceTabSkeleton";

const ProjectManagementFinance = () => {
  const { activeProjectId, baselineData, activeProjectData, totalCalculatedExpense } = useProjectManagementContext();
  const isInternalProject = isInternalManproProject(activeProjectData);
  
  const [termijns, setTermijns] = useState<TermijnDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTermijnId, setSelectedTermijnId] = useState<string | null>(null);
  const [expandedTermijnId, setExpandedTermijnId] = useState<string | null>(null);
  const [payoutNotes, setPayoutNotes] = useState('');
  
  const [bankDetails, setBankDetails] = useState<any>(null);

  const fetchData = async () => {
    if (!activeProjectId || isInternalProject) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const [termijnsData, bankResponse] = await Promise.all([
        providerTermijnService.getTermijns(activeProjectId as string),
        axiosInstance.get("/v1/provider/get-bank-details").catch(() => null)
      ]);
      setTermijns(termijnsData);
      if (bankResponse && bankResponse.data?.content) {
        setBankDetails(bankResponse.data.content);
      }
    } catch (error) {
      console.error("Failed to fetch finance data:", error);
      toast.error("Gagal memuat data keuangan.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeProjectId, isInternalProject]);

  const toggleAccordion = (id: string, status: string) => {
    if (status !== 'completed') return;
    setExpandedTermijnId(prev => prev === id ? null : id);
  };

  const handleRequestPayout = (termijnId: string) => {
    if (!activeProjectId) return;
    setSelectedTermijnId(termijnId);
    setPayoutNotes('');
    setIsModalOpen(true);
  };

  const confirmPayout = async () => {
    if (!activeProjectId || !selectedTermijnId) return;

    setIsModalOpen(false);
    try {
      const loadingToast = toast.loading("Mengajukan pencairan...");
      await providerTermijnService.requestPayout(activeProjectId as string, selectedTermijnId, payoutNotes);
      toast.success("Pengajuan berhasil dikirim!", { id: loadingToast });
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Gagal mengajukan pencairan");
    }
  };

  const totalRAB = baselineData?.budget ? Number(baselineData.budget) : 0;
  const spent = Number(totalCalculatedExpense || 0);
  const remaining = totalRAB - spent;

  if (isInternalProject) {
    return (
      <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 lg:p-8 w-full">
        <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4 border-b border-slate-100 pb-6">
          <div>
            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
              <Wallet className="text-slate-600" /> Anggaran internal
            </h3>
            <p className="text-sm text-slate-500">
              Proyek internal: tidak ada pembayaran klien dan tidak ada pencairan escrow Indoteknisi.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Budget RAB</div>
            <div className="text-xl font-black text-slate-800">Rp {totalRAB.toLocaleString("id-ID")}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Pengeluaran tercatat</div>
            <div className="text-xl font-black text-slate-800">Rp {spent.toLocaleString("id-ID")}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Sisa vs RAB</div>
            <div className={`text-xl font-black ${remaining < 0 ? "text-red-700" : "text-slate-800"}`}>
              Rp {remaining.toLocaleString("id-ID")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <FinanceTabSkeleton />;
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft': return <span className="bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-200">Draft</span>;
      case 'pending_payment': return <span className="bg-orange-100 text-orange-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-orange-200">Menunggu Transfer Klien</span>;
      case 'partial_payment': return <span className="bg-orange-100 text-orange-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-orange-300">Dicicil (Belum Hold Penuh)</span>;
      case 'escrowed': return <span className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">Dana Aman di Escrow</span>;
      case 'pending_owner_approval': return <span className="bg-yellow-100 text-yellow-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-yellow-200">Menunggu Validasi Klien</span>;
      case 'ready_for_payout': return <span className="bg-purple-100 text-purple-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-purple-200">Proses Pencairan Admin</span>;
      case 'completed': return <span className="bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-green-200 flex items-center gap-1"><CheckCircle size={14}/> Selesai (Cair)</span>;
      case 'work_revision': return <span className="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-200">Butuh Revisi Klien</span>;
      case 'refunded': return <span className="bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-300">Dana dikembalikan</span>;
      default: return <span className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200">{status}</span>;
    }
  };

  if (termijns.length === 0) {
    return (
      <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-10 w-full text-center flex flex-col items-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Wallet className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Belum Ada Tagihan Aktif</h3>
        <p className="text-slate-500 max-w-md">
          Jadwal pembayaran akan otomatis muncul di sini setelah Perencanaan (Baseline) disetujui secara resmi oleh Klien (Owner).
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 lg:p-8 w-full">
      <div className="flex flex-col md:flex-row justify-between md:items-center mb-8 gap-4 border-b border-slate-100 pb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
            <CircleDollarSign className="text-green-600" /> Tracking Pencairan & Escrow
          </h3>
          <p className="text-sm text-slate-500">Pantau status pembayaran klien dan cairkan dana escrow Anda.</p>
        </div>
        <div className="md:text-right bg-slate-50 p-4 rounded-xl border border-slate-200 inline-block">
          <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Total Nilai Proyek</div>
          <div className="text-2xl font-black text-slate-800">Rp {totalRAB.toLocaleString('id-ID')}</div>
        </div>
      </div>

        <div className="space-y-4">
          {termijns.map((t, i) => {
            const isCompleted = t.status === 'completed';
            const isExpanded = expandedTermijnId === t.id;
            const outboundPayment = (t as any).payments?.find((p: any) => p.type === 'outbound');

            return (
              <div 
                key={t.id} 
                className={`border rounded-2xl transition-all bg-white overflow-hidden relative ${isCompleted ? 'cursor-pointer hover:border-green-300 hover:shadow-md' : 'border-slate-200 hover:border-blue-300 hover:shadow-md'}`}
                onClick={() => toggleAccordion(t.id, t.status)}
              >
                
                {/* Status Line Indicator */}
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${t.status === 'completed' ? 'bg-green-500' : t.status === 'escrowed' ? 'bg-blue-500' : t.status === 'refunded' ? 'bg-slate-400' : 'bg-orange-400'}`}></div>
                
                {/* Header Row */}
                <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-6 relative">
                  <div className="flex gap-5 items-center pl-2">
                    <div className="w-14 h-14 rounded-full bg-blue-50 flex flex-col items-center justify-center border border-blue-100 text-blue-700">
                      <span className="text-xs font-bold leading-none">Termin</span>
                      <span className="text-lg font-black leading-none mt-1">{i + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-extrabold text-slate-800 text-lg">{t.title}</h4>
                      <div className="flex flex-col gap-1 mt-1.5">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-black text-slate-700">Rp {Number(t.amount).toLocaleString('id-ID')}</span>
                          <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                          <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">{t.percentage}%</span>
                        </div>
                        {t.status === 'partial_payment' && (
                          <div className="flex flex-col gap-1 mt-1">
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-1 rounded-md border border-green-100">
                                Cicilan terverifikasi: Rp {Number(t.total_paid || 0).toLocaleString('id-ID')}
                              </span>
                              <span className="text-xs font-bold text-orange-700 bg-orange-50 px-2 py-1 rounded-md border border-orange-100">
                                Belum Dibayar: Rp {Number(t.remaining_amount || 0).toLocaleString('id-ID')}
                              </span>
                            </div>
                            <p className="text-[10px] text-orange-600 font-medium">Klien baru membayar sebagian. Dana ini belum di-hold escrow penuh. Anda dapat mulai bekerja atas kebijakan sendiri, atau menunggu sisa pelunasan.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 md:pl-0 pl-2">
                    <div className="flex flex-col items-start md:items-end gap-2">
                      {getStatusBadge(t.status)}
                      
                      {t.status !== 'refunded' && (t.status === 'escrowed' || t.status === 'work_revision') && !isInternalProject && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRequestPayout(t.id); }}
                          className="mt-1 bg-green-600 text-white text-sm font-bold py-2 px-5 rounded-xl hover:bg-green-700 hover:shadow-lg transition-all"
                        >
                          Ajukan Pencairan Dana
                        </button>
                      )}
                    </div>
                    {isCompleted && (
                      <div className="text-slate-400 ml-2">
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                    )}
                  </div>
                </div>

                {/* Accordion Body */}
                {isExpanded && outboundPayment && (
                  <div className="border-t border-slate-100 bg-slate-50/50 p-5 pl-7">
                    <h5 className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-500" /> Detail Bukti Transfer Pencairan (Admin)
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-slate-500 font-bold mb-0.5">Bank Tujuan</p>
                          <p className="text-sm font-semibold text-slate-900">{outboundPayment.bank_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-bold mb-0.5">Nomor Rekening</p>
                          <p className="text-sm font-semibold text-slate-900">{outboundPayment.account_number || '-'}</p>
                          <p className="text-xs text-slate-500 mt-0.5">A.N. {outboundPayment.account_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-bold mb-0.5">Nominal Transfer</p>
                          <p className="text-sm font-black text-green-700">Rp {Number(outboundPayment.amount || t.amount).toLocaleString('id-ID')}</p>
                        </div>
                        {outboundPayment.notes && (
                          <div>
                            <p className="text-xs text-slate-500 font-bold mb-0.5">Catatan Admin</p>
                            <p className="text-sm text-slate-700 italic bg-white p-2 rounded-lg border border-slate-200">{outboundPayment.notes}</p>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <p className="text-xs text-slate-500 font-bold mb-1.5">Struk Bukti Transfer</p>
                        {outboundPayment.proof_url || outboundPayment.proof_file_path ? (
                          <a href={(outboundPayment as any).proof_url || outboundPayment.proof_file_path} target="_blank" rel="noopener noreferrer" className="block w-full max-w-[240px] rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:opacity-90 transition-opacity">
                            <img src={(outboundPayment as any).proof_url || outboundPayment.proof_file_path || ''} alt="Bukti Transfer" className="w-full h-auto object-contain bg-white" />
                          </a>
                        ) : (
                          <div className="w-full max-w-[240px] h-32 bg-slate-100 rounded-xl flex items-center justify-center border border-slate-200 border-dashed text-slate-400 text-sm">
                            Tidak ada struk terlampir
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Konfirmasi Pencairan Dana"
        size="md"
        footer={
          <div className="flex justify-end gap-3 w-full">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Batal
            </button>
            <button
              onClick={confirmPayout}
              disabled={!bankDetails}
              className={`px-4 py-2 text-sm font-bold text-white rounded-xl transition-colors ${bankDetails ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-300 cursor-not-allowed'}`}
            >
              Ya, Ajukan Pencairan
            </button>
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center text-center p-4">
          <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mb-4">
            <AlertCircle size={32} />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">Anda yakin ingin mengajukan pencairan?</h3>
          <p className="text-slate-600 text-sm mb-4">
            Pastikan pekerjaan lapangan sudah benar-benar selesai sesuai target termin ini.
          </p>
          <div className="w-full text-left mb-4">
            <label className="block text-xs font-bold text-slate-700 mb-1.5">Pesan untuk Admin (Opsional)</label>
            <textarea
              value={payoutNotes}
              onChange={e => setPayoutNotes(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
              rows={2}
              placeholder="Misal: Tolong cairkan untuk beli semen..."
            ></textarea>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left w-full">
            <p className="text-amber-800 text-xs font-bold mb-3">INFO REKENING PENCAIRAN</p>
            {bankDetails ? (
              <div className="bg-white border border-amber-100 rounded-lg p-3">
                <p className="text-sm font-semibold text-slate-800 mb-1">{bankDetails.bank_name}</p>
                <p className="text-lg font-mono font-bold text-slate-900 leading-none mb-1">{bankDetails.acc_no}</p>
                <p className="text-xs text-slate-500 uppercase">A.N. {bankDetails.acc_holder_name}</p>
              </div>
            ) : (
              <div className="bg-white border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                <p className="font-bold mb-2">Rekening Bank Belum Diisi!</p>
                <p className="text-xs text-red-600 mb-3">Sistem tidak menemukan informasi rekening untuk mencairkan dana. Anda tidak bisa melanjutkan proses ini sebelum melengkapinya.</p>
                <a href="https://indoteknisi.net/provider/bank-info" target="_blank" rel="noopener noreferrer" className="block text-center bg-red-600 text-white font-bold py-2 rounded-md hover:bg-red-700 transition-colors">
                  Atur Rekening Sekarang
                </a>
              </div>
            )}
            
            {bankDetails && (
              <p className="text-amber-900 text-xs mt-3 leading-relaxed">
                Sesuai prosedur Indoteknisi, dana akan diteruskan ke Rekening Bank Utama yang terdaftar di Profil Anda di atas setelah klien menyetujui hasil kerja.
              </p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectManagementFinance;
